<script lang="ts">
export default {
  name: 'CompaniesAnalysisQscas',
}
</script>

<script lang="ts" setup>
import { ref, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useStore } from 'vuex'
import moment from 'moment'
import { useCompaniesNotAvailable } from 'src/biz_spa/composables'
import { useQSCASentences } from 'src/biz_spa/domains/analysis/composables'
import CompaniesAnalysisLayout from 'src/biz_spa/layouts/CompaniesAnalysisLayout.vue'
import CompaniesAnalysisQscasHeader from 'src/biz_spa/components/CompaniesAnalysis/Qscas/Header.vue'
import CompaniesAnalysisCrossSearch from 'src/biz_spa/components/CompaniesAnalysis/CrossSearch.vue'
import CompaniesAnalysisQscasTable from 'src/biz_spa/components/CompaniesAnalysis/Qscas/Table.vue'
import KkSentencesModal from 'src/biz_spa/domains/analysis/components/KkSentencesModal/index.vue'
import { crossAnalysisTypes } from 'src/biz_spa/modules/analysis'
import Info from 'src/biz_spa/components/Info.vue'
import { actionEvent } from 'src/biz_spa/modules/googleAnalytics'
import type { CrossAnalysisType, QSCACategory } from 'src/biz_spa/types'

type QSCAQuery = {
  biz_companies_analysis_qsca_form: {
    start_time: string
    end_time: string
    data_type: string[]
  }
  analysis_axis: string
  cross_analysis_axis: string
  analysis_axis_id?: number
}

const route = useRoute()
const router = useRouter()
const store = useStore()

const modal = ref(false)
const hideNoData = ref(false)
const isVisibleSentencesModal = ref(false)
const qscaCategory = ref<QSCACategory>('QUALITY')

const companyUuid = computed(() => route.params.company_uuid as string)

const qscas = computed(() => store.state.companiesAnalysisQscas.data)
// NOTE: 言語別はこの分析画面で利用しないため削除
const axes = computed(() => store.state.companiesAnalysisAxes.data.filter((v: CrossAnalysisType) => !['locale'].includes(v.id)))
const axesLoading = computed(() => store.state.companiesAnalysisAxes.loading)
const searchAttributes = computed(() => store.state.companiesSearchTagGroups.data)
const formValues = computed(() => store.state.companiesAnalysisQscas.formValues)
const individualAnalysisField = computed<Record<string, string | number>>(() => store.state.companiesAnalysisQscas.individualAnalysisField)
const pageType = computed(() => store.state.companiesAnalysisQscas.pageType)

const qscasLoading = computed(() => store.getters['companiesAnalysisQscas/loading'])
const crossSearchField = computed(() => store.state.companiesAnalysisQscas.crossSearchField)
const crossSearchFields = computed(() => store.getters['companiesAnalysisQscas/crossSearchFields'])
const dataTypes = computed<string[]>(() => store.getters['companiesAnalysisQscas/dataTypes'])
const startTime = computed<string>(() => store.getters['companiesAnalysisQscas/startTime'])
const endTime = computed<string>(() => store.getters['companiesAnalysisQscas/endTime'])
const showMoreThanEditorAuthority = computed(() => store.getters['companiesAccessibleApi/showMoreThanEditorAuthority'])
const providedAiReviewSentencesAnalysis = computed<boolean>(() => store.getters['companiesCompany/providedAiReviewSentencesAnalysis'])

const { notAvailableType } = useCompaniesNotAvailable(crossSearchField, pageType)

const individualAnalysisName = computed(() => individualAnalysisField.value?.name || '')

const {
  isLoading: isSentencesLoading,
  positiveSentences,
  negativeSentences,
  readSentences,
} = useQSCASentences()

const query = computed<QSCAQuery>(() => ({
  biz_companies_analysis_qsca_form: {
    start_time: startTime.value,
    end_time: endTime.value,
    data_type: formValues.value.keyword_analysis_targets,
  },
  analysis_axis: pageType.value.fields,
  cross_analysis_axis: crossSearchField.value?.fields,
  analysis_axis_id: individualAnalysisField.value?.id as number,
}))

const readCompaniesAnalysisAxes = () => store.dispatch('companiesAnalysisAxes/readCompaniesAnalysisAxes')
const readSearchAttributes = () => store.dispatch('companiesSearchTagGroups/readSearchTagGroups', companyUuid.value)
const setFormValues = (values: { keyword_analysis_targets: string[] }) => store.dispatch('companiesAnalysisQscas/setFormValues', values)
const setPageType = (value: CrossAnalysisType) => store.dispatch('companiesAnalysisQscas/setPageType', value)
const readAnalysisQscas = (query: QSCAQuery) => store.dispatch('companiesAnalysisQscas/readAnalysisQscas', query)
const initAnalysisQscas = () => store.dispatch('companiesAnalysisQscas/initAnalysisQscas')
const setCrossSearchField = (values: CrossAnalysisType | null) => store.dispatch('companiesAnalysisQscas/setCrossSearchField', values)
const setSelectedYear = (value: number) => store.dispatch('companiesAnalysisQscas/setSelectedYear', value)
const setSelectedMonth = (value: number) => store.dispatch('companiesAnalysisQscas/setSelectedMonth', value)
const setSelectedPeriod = (value: number) => store.dispatch('companiesAnalysisQscas/setSelectedPeriod', value)
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const setIndividualAnalysisField = (values: any) => store.dispatch('companiesAnalysisQscas/setIndividualAnalysisField', values)
const setProvidedAiReviewSentencesAnalysis = (value: boolean) => store.dispatch('companiesAnalysisQscas/setProvidedAiReviewSentencesAnalysis', value)

const resetIndividualAnalysis = () => {
  if (qscasLoading.value) return
  setIndividualAnalysisField(null)
  readAnalysisQscas(query.value)
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const changeIndividualAnalysisField = (field: any) => {
  if (qscasLoading.value) return
  setIndividualAnalysisField(field)
  readAnalysisQscas(query.value)
}

const initFilterValues = () => {
  setFormValues({
    keyword_analysis_targets: searchAttributes.value.keyword_analysis_targets.tags.map((v: { name: string; id: string; value: string }) => v.value)
  })
}

const handleChangeDateRange = ({ value, type }: { value: string; type: string }) => {
  switch (type) {
  case 'period':
    setSelectedPeriod(Number(value))
    break
  case 'month':
    setSelectedMonth(Number(value))
    break
  case 'year':
    setSelectedYear(Number(value))
    break
  }
  readAnalysisQscas(query.value)
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const handleDetailIconClick = (item: any) => {
  let routeObj: { name: string; params: { company_uuid: string }; query: Record<string, string> } = {
    name: 'CompaniesAnalysisQscasDetail',
    params: { company_uuid: companyUuid.value },
    query: {
      analysis_axis: pageType.value.fields,
      analysis_axis_id: item.fields[0].id,
    }
  }

  if (crossSearchField.value) {
    routeObj.query = {
      ...routeObj.query,
      cross_analysis_axis: crossSearchField.value.fields,
      cross_analysis_axis_id: item.fields[1].id,
    }
  }

  router.push(routeObj)
}

const changePageType = async (value: CrossAnalysisType) => {
  if (qscasLoading.value) return
  setIndividualAnalysisField(null)
  setPageType(value)
  readAnalysisQscas(query.value)
}

const changeCrossSearchField = async (value: CrossAnalysisType) => {
  if (qscasLoading.value) return
  setIndividualAnalysisField(null)
  setCrossSearchField(value)
  readAnalysisQscas(query.value)
}

const getActionEventValue = (category: QSCACategory) => {
  switch (category) {
  case 'QUALITY':
    return 'q'
  case 'SERVICE':
    return 's'
  case 'CLEANLINESS':
    return 'c'
  case 'ATMOSPHERE':
    return 'a'
  }
}

const openSentenceModal = async ({ category, uuids }: { category: QSCACategory; uuids: string[] }) => {
  qscaCategory.value = category
  isVisibleSentencesModal.value = true
  actionEvent('QscaSentimentModal', getActionEventValue(category))
  await readSentences({
    category,
    startDate: moment(startTime.value),
    endDate: moment(endTime.value),
    storeUuids: uuids,
  })
}

const initialized = async () => {
  setIndividualAnalysisField(null)
  setProvidedAiReviewSentencesAnalysis(providedAiReviewSentencesAnalysis.value)
  initAnalysisQscas()
  setPageType(crossAnalysisTypes[2])
  setCrossSearchField(null)
  await Promise.all([
    readCompaniesAnalysisAxes(),
    readSearchAttributes()
  ])
  initFilterValues()
  readAnalysisQscas(query.value)
}

initialized()
</script>

<template>
  <CompaniesAnalysisLayout
    :not-available-type="notAvailableType"
    :company-uuid="companyUuid"
    :is-more-than-editor="showMoreThanEditorAuthority"
  >
    <template #header>
      <CompaniesAnalysisQscasHeader
        :loading="qscasLoading"
        @open-modal="modal = true"
        @on-date-range-changed="handleChangeDateRange"
      />
    </template>
    <template #terms>
      <CompaniesAnalysisCrossSearch
        v-if="!axesLoading"
        :disabled="qscasLoading"
        :cross-search-field="crossSearchField"
        :current-page-type="pageType"
        :page-types="axes"
        @set-page-type="changePageType"
        @set-cross-search-field="changeCrossSearchField"
      />
    </template>
    <template #reset>
      <ul v-show="individualAnalysisName" class="c-links">
        <li class="c-links__item">
          <strong @click="resetIndividualAnalysis">{{ $t('pages.companies.analysis.individual.reset') }}</strong> | {{ $t('pages.companies.analysis.individual.listTitle', { name: individualAnalysisName }) }}
        </li>
      </ul>
      <ul v-show="!qscasLoading && !individualAnalysisField" class="c-radio">
        <li class="c-radio__item">
          <input id="checkbox_hide_no_data"
                 v-model="hideNoData"
                 class="c-radio__input"
                 type="checkbox"
          >
          <label class="c-radio__label" for="checkbox_hide_no_data">
            {{ $t('pages.companies.analysis.qscas.hideNoData') }}
          </label>
        </li>
      </ul>
    </template>
    <template #table>
      <Info>
        <p>{{ $t('pages.companies.analysis.qscas.allDataInfo') }}</p>
      </Info>
      <CompaniesAnalysisQscasTable
        :hide-no-data="hideNoData"
        :loading="qscasLoading"
        :cross-search-fields="crossSearchFields"
        :static-heads="dataTypes"
        :items="qscas"
        :truncate-store-name="false"
        @detailIconClicked="handleDetailIconClick"
        @change-individual-analysis-field="changeIndividualAnalysisField"
        @click-score="openSentenceModal"
      />
      <KkSentencesModal
        v-model:is-visible="isVisibleSentencesModal"
        :positive="positiveSentences"
        :negative="negativeSentences"
        :qsca-category="qscaCategory"
        :is-loading="isSentencesLoading"
      />
    </template>
  </CompaniesAnalysisLayout>
</template>
